import React, { Suspense, useEffect } from 'react';
import { useQuery } from "react-query";
import * as apiClient from "../api-client";
// Import the CSS
import '../index.css';
import Loader from '../components/LoaderComponent';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import FAQHoneymoon from '../components/HolidayFAQs/HoneymoonFaq';
import BlogSliderType from '../components/Blogs/BlogSliderType';
import HolidayTypeHotelsList from '../components/HolidayTypeHotelList';
import Slider1 from "../components/CountrySliders/Maldives/HoneymoonSlider";
import Slider2 from "../components/CountrySliders/Mauritius/HoneymoonSlider";
import HotelSliderSkeleton from '../components/Skeletons/HotelSliderSkeleton';
import ReadMoreLines from '../components/ReadMoreLines';

// Lazy load the components
// const LazyLatestDestinationCard = React.lazy(() => import('../components/LastestDestinationCard'));
const LazyWhyChoose = React.lazy(() => import('../components/WhyChoose/Honeymoon'));
const LazyVacationTypes = React.lazy(() => import('../components/Home/VacationTypes'));

const PageHoneymoon = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: hotels } = useQuery("fetchAllInclusiveHotels", apiClient.fetchHotels);

  // const [isExpanded, setIsExpanded] = useState(false);
  // const toggleText = () => setIsExpanded(!isExpanded);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Honeymoon Holidays & Romantic Getaways for Newlyweds | Halo Holidays</title>
          <meta name="description" content="Discover our handpicked honeymoon resorts, bespoke itineraries, and romantic destinations for an unforgettable start to your new life together." />
          {/* <meta name="keywords" content="travel, hotels, holidays, deals, holiday packages" /> */}
        </Helmet>
      </HelmetProvider>

      <section className="max-h-640 md:min-h-0 lg:min-h-0 my-8">
        <hr className="mb-10" />
        <h2 className="text-2xl font-semibold items-center justify-center flex mt-10">More reasons to choose us</h2>
        <Suspense fallback={<Loader />}>
          <LazyWhyChoose />
        </Suspense>
        <hr className="mb-10 mt-10" />
      </section>

      <section className="mx-4 my-4 md:mx-0 md:my-0">

        <div className='mt-2 mb-4'>
          <h1 className="text-2xl font-semibold">Honeymoon Holidays</h1>
          <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
            <p className="text-black md:mb-1 md:mt-2">
              Your honeymoon is a once-in-a-lifetime experience, a chance to celebrate your love and embark on a new adventure together as you begin a new chapter of your lives. Halo Holidays understands the significance of this special occasion, and we're dedicated to creating unforgettable honeymoon holidays that exceed your expectations.
              <br /><br />
              Imagine yourselves strolling hand-in-hand along the pristine beaches of the Maldives, where the soft white sand and crystal-clear waters create a picture-perfect backdrop for romance. Picture yourselves sipping champagne under a canopy of stars, lost in each other's embrace as the gentle waves serenade you. Or perhaps you envision yourselves exploring exotic destinations filled with rich culture and natural beauty, from the vibrant markets of Marrakech to the lush jungles of Bali.
              <br /><br />
              Whether you're seeking a relaxing beach escape on a <a href='https://www.haloholidays.co.uk/destinations/maldives' className='text-halo-pink'>holiday to Maldives</a>, an adventurous safari in South Africa, a cultural immersion in Japan, or a gastronomic journey through Italy, we have the perfect honeymoon destination to match your desires. Our expert travel advisors will work closely with you to create a personalized itinerary that includes romantic activities like couples' massages and private sunset cruises, intimate dining experiences at world-class restaurants, and special touches like rose petal baths and champagne breakfasts that will make your honeymoon truly magical.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Honeymoon Holiday Resorts Where Dreams Come True</h2>
            <p className='text-black'>
              Unwind in luxurious honeymoon holiday resorts designed to ignite romance and create lasting memories. From private plunge pools and candlelit dinners to couples' massages and sunset cruises, our handpicked havens offer the perfect blend of intimacy and indulgence. Whether you prefer the overwater bungalows of the Maldives, the sun-kissed shores of the Mediterranean, or the exotic allure of Southeast Asia, our honeymoon holiday resorts cater to every couple's desires. Looking to save on your dream getaway? Check out our exclusive deals on <a href='https://www.haloholidays.co.uk/couple-holidays' className='text-halo-pink'>couple holidays</a> and discover the perfect romantic retreat for your budget.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Not in the Honeymoon Phase? No Problem, We Have Other Options</h2>
            <p className='text-black'>
              Planning a different type of getaway? Halo Holidays offers a diverse range of holiday options beyond honeymoon holidays. Discover our exciting <a href='https://www.haloholidays.co.uk/family-holidays' className='text-halo-pink'>family holidays</a> with kid-friendly resorts and activities, perfect for creating unforgettable memories with your loved ones. Or, if you're seeking a budget-friendly escape, explore our selection of all-inclusive holidays that offer exceptional value for money, with flights, accommodation, meals, drinks, and activities all included in one convenient price.
            </p>
          </ReadMoreLines>
        </div>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='mt-6 -mb-2 md:mt-0 md:mb-2'>
              <h2 className="text-xl font-semibold">Maldives Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Honeymoon" holidayCountry="Maldives" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider1 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='md:mt-4 -mb-2 md:mb-2'>
              <h2 className="text-xl font-semibold">Mauritius Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Honeymoon" holidayCountry="Mauritius" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider2 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <FAQHoneymoon />

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">Latest All Inclusive Destinations</h2>
      <p>Most recent all-inclusive destinations added by our hosts</p>
      </Suspense> */}
        {/* Displaying the top row hotels */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div>
       */}
        {/* Displaying the bottom row hotels */}
        {/* <div className="grid md:grid-cols-3 gap-4">
        {bottomRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}

        <Suspense fallback={<Loader />}>
          <div className='p-4 md:p-0 md:mt-12 md:mb-12'>
            <LazyVacationTypes />
          </div>
        </Suspense>

        <section className='p-4 md:p-0'>
          <h2 className='text-2xl font-semibold mb-4'>Related Blogs</h2>
          <BlogSliderType blogType='love_and_travel' />
        </section>

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">More All Inclusive Destinations</h2>
      <p>Explore more all-inclusive destinations</p>
      </Suspense> */}

        {/* Optionally, display more hotels with AllInclusiveHotelCard */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyAllInclusiveHotelCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}
      </section>
    </>
  );
};

export default PageHoneymoon;
